import React, { useEffect, useRef, useState } from "react";
import { audioSupported } from "../lib/keyboardPlan";
import { nextStreak } from "../lib/numberSenseData";
import {
  scaleType,
  teachSlides,
  drillKind,
  buildDrillSession,
  applySessionResults,
  sessionXp,
  practiceXp,
  recordDrillRun,
  drillStatOf,
  timerUnlocked,
  foldAnswerStreak,
  levelOf,
  conceptId,
  MASTERY_MAX,
  DEFAULT_LENGTH,
  MIX_KEY,
  WALK_XP,
} from "../lib/numberSenseV2";
import HomeScreen from "../components/numberSenseV2/HomeScreen";
import DrillMenuScreen from "../components/numberSenseV2/DrillMenuScreen";
import WalkScreen from "../components/numberSenseV2/WalkScreen";
import ChordWalkScreen from "../components/numberSenseV2/ChordWalkScreen";
import InvWalkScreen from "../components/numberSenseV2/InvWalkScreen";
import TeachScreen from "../components/numberSenseV2/TeachScreen";
import KeyScreen from "../components/numberSenseV2/KeyScreen";
import DrillScreen from "../components/numberSenseV2/DrillScreen";
import DoneScreen from "../components/numberSenseV2/DoneScreen";
import "../styles/numbersense.css";
import "../styles/numbersense-v2.css";

/* ============================================================
   Number Sense v2 — the scale-type-first remix of /number-sense.
   Instead of v1's one-unit-per-key curriculum, each scale type is
   taught once (its step recipe) and drilled Duolingo-style in any
   key you pick, with per-(type, key) crown levels.

   Orchestrator only: owns persisted progress (localStorage — v2 is
   a beta and stays device-local) plus the screen state machine
   (home | teach | keys | walk | chordwalk | invwalk | menu | drill |
   done), and routes to the screen components in
   components/numberSenseV2/.
   ============================================================ */

const LOCAL_KEY = "numberSenseV2Progress";
const CAN_PLAY = audioSupported();

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

const loadProgress = () => {
  try {
    const saved = localStorage.getItem(LOCAL_KEY);
    if (saved) {
      const d = JSON.parse(saved);
      return {
        xp: d.xp || 0,
        streak: d.streak || 0,
        lastDate: d.lastDate || "",
        learned: d.learned || {},
        mastery: d.mastery || {},
        drillStats: d.drillStats || {},
        answerStreak: d.answerStreak || { current: 0, best: 0 },
        built: d.built || {},
        chordBuilt: d.chordBuilt || {},
        invBuilt: d.invBuilt || {},
      };
    }
  } catch {
    /* ignore — start fresh */
  }
  return {
    xp: 0,
    streak: 0,
    lastDate: "",
    learned: {},
    mastery: {},
    drillStats: {},
    answerStreak: { current: 0, best: 0 },
    built: {},
    chordBuilt: {},
    invBuilt: {},
  };
};

const NumberSenseV2 = () => {
  const [progress, setProgress] = useState(loadProgress);
  const [screen, setScreen] = useState("home");
  const [typeId, setTypeId] = useState(null);
  const [keyName, setKeyName] = useState(null);
  const [drill, setDrill] = useState({ kind: "mixed", length: DEFAULT_LENGTH });
  const [session, setSession] = useState(null);
  const [doneInfo, setDoneInfo] = useState(null);
  const progressRef = useRef(progress);
  progressRef.current = progress;

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(progress));
    } catch {
      /* ignore */
    }
  }, [progress]);

  const type = typeId ? scaleType(typeId) : null;

  const openLearn = (t) => {
    setTypeId(t.id);
    setScreen("teach");
  };

  // Drill goes through learn the first time — Duolingo's "teach, then drill".
  const openDrill = (t) => {
    setTypeId(t.id);
    setScreen(progress.learned[t.id] ? "keys" : "teach");
  };

  const finishTeach = () => {
    setProgress((p) => ({ ...p, learned: { ...p.learned, [typeId]: true } }));
    setScreen("keys");
  };

  // A key you've never built goes through the guided walk first — you should
  // have played the scale once before the drills quiz you on it. Mixed-key
  // sessions skip it (no single scale to walk).
  const openMenu = (pickedKey) => {
    setKeyName(pickedKey);
    const needsWalk =
      pickedKey !== MIX_KEY && !progress.built[conceptId(typeId, pickedKey)];
    setScreen(needsWalk ? "walk" : "menu");
  };

  const finishWalk = () => {
    const p = progressRef.current;
    const id = conceptId(typeId, keyName);
    if (!p.built[id]) {
      setProgress({
        ...p,
        xp: p.xp + WALK_XP,
        built: { ...p.built, [id]: true },
      });
    }
    setScreen("menu");
  };

  // A chord drill in a key whose chords you've never stacked routes through
  // the guided chord walk first, same as keys route through the scale walk.
  const startDrill = (kind, length) => {
    setDrill({ kind, length });
    const def = drillKind(kind);
    if (
      def &&
      def.group === "chords" &&
      keyName !== MIX_KEY &&
      !progress.chordBuilt[conceptId(typeId, keyName)]
    ) {
      setScreen("chordwalk");
      return;
    }
    if (
      def &&
      def.group === "inversions" &&
      keyName !== MIX_KEY &&
      !progress.invBuilt[conceptId(typeId, keyName)]
    ) {
      setScreen("invwalk");
      return;
    }
    setSession(
      buildDrillSession(typeId, keyName, { kind, length, canPlay: CAN_PLAY })
    );
    setScreen("drill");
  };

  const finishChordWalk = () => {
    const p = progressRef.current;
    const id = conceptId(typeId, keyName);
    if (!p.chordBuilt[id]) {
      setProgress({
        ...p,
        xp: p.xp + WALK_XP,
        chordBuilt: { ...p.chordBuilt, [id]: true },
      });
    }
    setScreen("menu");
  };

  // Same guided-first-time gate as the chord walk, for a key's flips.
  const finishInvWalk = () => {
    const p = progressRef.current;
    const id = conceptId(typeId, keyName);
    if (!p.invBuilt[id]) {
      setProgress({
        ...p,
        xp: p.xp + WALK_XP,
        invBuilt: { ...p.invBuilt, [id]: true },
      });
    }
    setScreen("menu");
  };

  const finishDrill = (passed, { hearts, correct, total, results, totalMs }) => {
    const p = progressRef.current;
    const today = todayKey();
    const practice = Boolean(session && session.practice);
    const acc = total ? Math.round((correct / total) * 100) : 0;
    const answerStreak = foldAnswerStreak(p.answerStreak, results);
    const newBestStreak =
      answerStreak.best > ((p.answerStreak && p.answerStreak.best) || 0);
    const before = p.mastery;
    const mastery = applySessionResults(before, results, today);
    // Which keys moved up (for the done screen's level-up chips)?
    const levelUps = [];
    new Set(results.filter((r) => r.keyName).map((r) => r.keyName)).forEach(
      (k) => {
        const now = levelOf(mastery, typeId, k);
        const was = (before[conceptId(typeId, k)] || {}).strength || 0;
        if (now > was)
          levelUps.push({ keyName: k, level: now, crowned: now >= MASTERY_MAX });
      }
    );
    // A completed run (practice always completes; mixed only when passed)
    // stamps the drill record and the streak.
    const finished = practice || passed;
    const prevStat = drillStatOf(p.drillStats, typeId, keyName, drill.kind);
    const drillStats = finished
      ? recordDrillRun(p.drillStats, typeId, keyName, drill.kind, acc, totalMs)
      : p.drillStats;
    // Timing surfaces only once this drill was already unlocked going in; the
    // run that crosses the threshold announces the unlock instead.
    const wasTimed = timerUnlocked(p.drillStats, typeId, keyName, drill.kind);
    const nowTimed = timerUnlocked(drillStats, typeId, keyName, drill.kind);
    const award = practice ? practiceXp(acc) : passed ? sessionXp(hearts) : 0;
    setProgress({
      ...p,
      xp: p.xp + award,
      streak: finished ? nextStreak(p.streak, p.lastDate, today) : p.streak,
      lastDate: finished ? today : p.lastDate,
      mastery,
      drillStats,
      answerStreak,
    });
    setDoneInfo(
      finished
        ? {
            passed: true,
            practice,
            award,
            acc,
            hearts,
            levelUps,
            runs: (prevStat ? prevStat.runs : 0) + 1,
            newBest: !prevStat || acc > prevStat.best,
            timed: wasTimed,
            timerJustUnlocked: nowTimed && !wasTimed,
            totalMs,
            newBestTime:
              wasTimed &&
              acc >= 100 &&
              (!prevStat || !prevStat.bestMs || totalMs < prevStat.bestMs),
            answerStreak,
            newBestStreak,
          }
        : { passed: false, correct, total, answerStreak, newBestStreak }
    );
    setScreen("done");
  };

  if (screen === "teach" && type)
    return (
      <TeachScreen
        type={type}
        slides={teachSlides(type)}
        onDone={finishTeach}
        onClose={() => setScreen("home")}
      />
    );

  if (screen === "keys" && type)
    return (
      <KeyScreen
        type={type}
        mastery={progress.mastery}
        onPick={openMenu}
        onClose={() => setScreen("home")}
      />
    );

  if (screen === "walk" && type && keyName)
    return (
      <WalkScreen
        type={type}
        keyName={keyName}
        onDone={finishWalk}
        onClose={() => setScreen("keys")}
      />
    );

  if (screen === "chordwalk" && type && keyName)
    return (
      <ChordWalkScreen
        type={type}
        keyName={keyName}
        onDone={finishChordWalk}
        onClose={() => setScreen("menu")}
      />
    );

  if (screen === "invwalk" && type && keyName)
    return (
      <InvWalkScreen
        type={type}
        keyName={keyName}
        onDone={finishInvWalk}
        onClose={() => setScreen("menu")}
      />
    );

  if (screen === "menu" && type && keyName)
    return (
      <DrillMenuScreen
        type={type}
        keyName={keyName}
        mastery={progress.mastery}
        drillStats={progress.drillStats}
        onStart={startDrill}
        onWalk={() => setScreen("walk")}
        onChordWalk={() => setScreen("chordwalk")}
        onInvWalk={() => setScreen("invwalk")}
        onClose={() => setScreen("keys")}
      />
    );

  if (screen === "drill" && session)
    return (
      <DrillScreen
        key={`${typeId}:${keyName}:${session.questions[0]?.prompt}`}
        session={session}
        timed={timerUnlocked(progress.drillStats, typeId, keyName, drill.kind)}
        startStreak={progress.answerStreak.current}
        onClose={() => setScreen("menu")}
        onFinish={finishDrill}
      />
    );

  if (screen === "done" && doneInfo && type)
    return (
      <DoneScreen
        info={doneInfo}
        type={type}
        keyName={keyName}
        drillKindId={drill.kind}
        onHome={() => setScreen("home")}
        onAgain={() => startDrill(drill.kind, drill.length)}
        onPickKey={() => setScreen("menu")}
      />
    );

  return (
    <HomeScreen progress={progress} onLearn={openLearn} onDrill={openDrill} />
  );
};

export default NumberSenseV2;
